import type uz from './uz'

/** English. Typed against the Uzbek source so a missing key is a build error, not a blank label. */
const en: typeof uz = {
  app: {
    name: 'Ombor',
    tagline: 'Warehouse & distribution system',
  },
  common: {
    save: 'Save',
    cancel: 'Cancel',
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    deleteConfirm: 'Delete “{name}”? It leaves the lists; its documents stay in history.',
    search: 'Search',
    loading: 'Loading…',
    unsavedWarning: 'You have unsaved changes. Leave the page?',
    draftFound: 'An unsaved draft was found.',
    restoreDraft: 'Restore',
    discardDraft: 'Discard',
    print: 'Print',
    undo: 'Undo',
    retry: 'Retry',
    close: 'Close',
    yes: 'Yes',
    no: 'No',
    actions: 'Actions',
    active: 'Active',
    inactive: 'Inactive',
    never: 'Never',
    back: 'Back',
    of: 'of',
    total: 'Total',
    post: 'Confirm',
    confirm: 'Confirm',
    cancelDocument: 'Cancel document',
    reason: 'Reason',
    note: 'Note',
    moreResults: '{count} more — refine the search',
    daysShort: '{n} day | {n} days',
    leave: 'Leave',
  },
  nav: {
    dashboard: 'Dashboard',
    warehouse: 'Warehouse',
    receipts: 'Goods receipts',
    shipments: 'Shipments',
    stock: 'Stock on hand',
    suppliers: 'Suppliers',
    agents: 'Agents',
    stores: 'Stores',
    payments: 'Cash desk',
    reports: 'Reports',
    settings: 'Settings',
    users: 'Users',
    sessions: 'Sessions',
    main: 'Main menu',
    collapse: 'Collapse menu',
    expand: 'Expand menu',
    search: 'Search',
    kassa: 'Kassa',
    logout: 'Log out',
    theme: 'Theme',
    language: 'Language',
  },
  auth: {
    loginTitle: 'Sign in',
    loginSubtitle: 'Sign in to your account',
    username: 'Username',
    password: 'Password',
    rememberMe: 'Remember me',
    submit: 'Sign in',
    submitting: 'Signing in…',
    loggedInAs: 'Signed in as',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Repeat new password',
    passwordChanged: 'Password changed. Please sign in again.',
    mustChangePassword: 'For security, please change your password.',
    lockedUntil: 'Account locked until {time}.',
    logoutAll: 'Sign out everywhere',
    sessionsTitle: 'Active sessions',
    currentSession: 'Current device',
    revokeSession: 'End session',
    lastSeen: 'Started',
    expiresAt: 'Expires',
    device: 'Device',
    ipAddress: 'IP address',
    revokeConfirm: 'End this session? That device will have to sign in again.',
    usernameRequired: 'Enter your username',
    passwordRequired: 'Enter your password',
  },
  users: {
    title: 'Users',
    create: 'New user',
    edit: 'Edit user',
    fullName: 'Full name',
    phone: 'Phone',
    role: 'Role',
    phoneHint:
      'The Telegram bot identifies people by this number. Without one, they cannot use it.',
    noPhone: 'no number',
    noPhoneWhy: 'This person cannot use the Telegram bot — no number on the account.',
    status: 'Status',
    lastLogin: 'Last sign-in',
    resetPassword: 'Reset password',
    resetPasswordHint:
      'A new password is set and all of the user’s sessions are ended. They must change it at first sign-in.',
    deactivate: 'Deactivate',
    deactivateConfirm: 'Deactivate «{name}»? They will no longer be able to sign in.',
    activate: 'Activate',
    created: 'User created',
    updated: 'User updated',
    passwordReset: 'Password reset. {count} session(s) ended.',
    empty: 'No users found.',
    searchPlaceholder: 'Search by username or full name',
  },
  roles: {
    ADMIN: 'Director',
    MANAGER: 'Manager',
    STOREKEEPER: 'Storekeeper',
    ACCOUNTANT: 'Accountant',
    AGENT: 'Agent',
    VIEWER: 'Viewer',
  },
  theme: {
    light: 'Light',
    dark: 'Dark',
    system: 'System',
  },
  table: {
    empty: 'Nothing found.',
    columns: 'Columns',
    density: 'Density',
    previous: 'Previous',
    next: 'Next',
    clearFilters: 'Clear filters',
    range: '{start}–{end} of {total}',
  },
  currency: {
    UZS: 'UZS',
  },
  validation: {
    invalidNumber: 'Invalid number',
    lineIncomplete: 'Line incomplete: {fields}',
    exceedsStock: 'More than is in stock',
    fixLines: '{count} line(s) incomplete or invalid. Fix or remove them.',
  },
  catalogue: {
    expiryTitle: 'Expiry warnings',
    expiryHint: 'How many days ahead to warn. The rings nest: red < amber < watch.',
    expiryWholeDays: 'Days must be whole numbers above zero.',
    expiryRedBelowAmber: 'The red limit must be below the amber one.',
    expiryAmberBelowWatch: 'The amber limit must be below the watch limit.',
    criticalDays: 'Red (days)',
    warningDays: 'Amber (days)',
    watchDays: 'Watch (days)',
    expirySaved: 'Expiry boundaries saved',
    products: 'Products',
    newProduct: 'New product',
    editProduct: 'Edit product',
    sku: 'SKU',
    name: 'Name',
    barcode: 'Barcode',
    category: 'Category',
    baseUnit: 'Base unit',
    baseUnitHint: 'The smallest sellable unit. All stock is stored in it.',
    baseUnitLocked: 'The base unit cannot change: the product has packages or stock history.',
    defaultSalePrice: 'Default sale price',
    minLevel: 'Minimum stock',
    maxLevel: 'Maximum stock',
    shelfLife: 'Shelf life (days)',
    returnableFromStore: 'A shop may return it',
    returnableToSupplier: 'The supplier takes it back',
    note: 'Note',
    packaging: 'Units and packaging',
    packagingHint: 'The base unit is present automatically. List only packages here.',
    packageUnit: 'Unit',
    packageFactor: 'Base units per package',
    packageBarcode: 'Barcode',
    purchaseDefault: 'Default for receipts',
    saleDefault: 'Default for shipments',
    addPackage: 'Add package',
    removePackage: 'Remove',
    packagePreview: '1 {unit} = {factor} {base}',
    units: 'Units',
    categories: 'Categories',
    currencies: 'Currencies',
    rates: 'Rates',
    warehouses: 'Warehouses',
    locations: 'Blocks',
    warehouseName: 'Warehouse name',
    warehouseAddress: 'Address',
    isDefault: 'Default',
    makeDefault: 'Make default',
    blockCode: 'Block code',
    blockName: 'Block name',
    addBlock: 'Add block',
    noBlocks: 'No blocks in this warehouse yet',
    defaultHint: 'Goods land here when a document names no warehouse',
    code: 'Code',
    nameUz: 'Name (uz)',
    nameRu: 'Name (ru)',
    decimalPlaces: 'Decimal places',
    symbol: 'Symbol',
    isBase: 'Base',
    makeBase: 'Make base',
    makeBaseConfirm:
      'Make «{code}» the base currency? Every rate and amount is then counted against it.',
    rate: 'Rate',
    effectiveDate: 'Date',
    setRate: 'Set rate',
    parent: 'Parent category',
    systemUnit: 'System unit',
    importTitle: 'Import from Excel',
    importHint: 'A dry run is checked first, then you confirm.',
    importPreview: 'Check',
    importConfirm: 'Confirm import',
    importResult: '{created} to create · {skipped} skipped',
    importDone: '{created} products created.',
    importRow: 'Row {row}',
  },
  parties: {
    storeStatus: {
      PENDING: 'Waiting to be confirmed',
      ACTIVE: 'Working',
      CLOSED: 'Closed',
    },
    allStatuses: 'All statuses',
    visitEvery: 'Expected every, days',
    visitEveryHint: 'Blank uses the company default (Settings → Field work)',
    location: 'Location',
    locationHint:
      'Paste a Google Maps or OpenStreetMap link, or the coordinates: 41.311081, 69.240562',
    locationNow: 'Currently',
    locationMissing: 'This shop is not on the map yet.',
    locationCheck: 'Check it on the map',
    locationUnreadable: 'No coordinates could be read from that link.',
    locationShortLink:
      'A shortened link carries no coordinates. Long-press the point in Google Maps — the coordinates appear, copy those.',
    locationReplacesAgent: 'This replaces a position an agent set.',
    placedByAgent: 'Set by the agent',
    placedByOffice: 'Set by the office',
    confirmStore: 'Confirm',
    storeConfirmed: '“{name}” confirmed',
    openedByAgent: 'Opened by an agent',
    onMap: 'On the map',
    rating: 'Rating',
    ratingLabel: 'Payment rating: {stars} of 5',
    lateCount: 'Late payments: {count}',
    relocationPending: 'Reported as moved',
    relocationTitle: 'Agent: the shop has moved',
    relocationDistance: '{m} m from the old position',
    relocationAccuracy: 'GPS accuracy: ±{m} m',
    relocationAccept: 'Accept',
    relocationReject: 'Reject',
    relocationRejectConfirm: 'Reject the report? The shop’s position stays as it is.',
    relocationAccepted: 'The shop’s position was updated',
    relocationRejected: 'The report was rejected',
    agentStatus: {
      ACTIVE: 'Working',
      BLOCKED: 'On leave',
      FIRED: 'Left',
    },
    assignmentReason: {
      INITIAL: 'Initial',
      FIRED: 'Left the company',
      REDISTRIBUTION: 'Redistribution',
      PERFORMANCE: 'Performance',
      VACATION: 'Leave',
    },
    changeStatus: 'Change status',
    statusReason: 'Reason',
    statusReasonHint: 'Why this person is off the round — this line answers it three months later.',
    statusChanged: 'Status changed',
    transfer: 'Transfer shops',
    transferTo: 'To',
    transferDate: 'Effective from',
    transferNote: 'Note',
    transferStores: 'Shops to move',
    transferSummary: '{count} shops → {agent}',
    transferred: '{count} shops moved',
    noStoresToTransfer: 'This agent has no shops.',
    selectAll: 'Select all',
    agentHistory: 'Agent history',
    roundHistory: 'Shop history',
    since: 'From',
    until: 'To',
    currentAssignment: 'Current',
    store: 'Shop',
    suppliers: 'Suppliers',
    agents: 'Agents',
    stores: 'Stores',
    newSupplier: 'New supplier',
    newAgent: 'New agent',
    newStore: 'New store',
    code: 'Code',
    fullName: 'Full name',
    name: 'Name',
    phone: 'Phone',
    phoneSecondary: 'Second phone',
    address: 'Address',
    agent: 'Agent',
    pickUser: 'Choose a user',
    nameFromAccount: 'The name and phone come from the account. Edit the user to change them.',
    creditLimit: 'Credit limit',
    openingBalance: 'Opening balance',
    openingBalanceHint: 'Debt from paper records. From Phase 5 balances are computed from it.',
    storeCount: 'Stores',
    note: 'Note',
  },
  warehouse: {
    margin: 'Margin',
    updateCatalogue: "Update the product's price too",
    draftSaved: 'Draft saved',
    postedToast: '{number} posted',
    receipts: 'Goods receipts',
    newReceipt: 'New receipt',
    editReceipt: 'Edit receipt',
    stock: 'Stock on hand',
    docNumber: 'Document №',
    batch: 'Batch',
    date: 'Date',
    supplier: 'Supplier',
    lines: 'Lines',
    totalAmount: 'Total',
    currency: 'Currency',
    status: 'Status',
    createdBy: 'Created by',
    product: 'Product',
    quantity: 'Quantity',
    unit: 'Unit',
    unitConversion: '{entered} = {base}',
    purchasePrice: 'Purchase price',
    salePrice: 'Sale price',
    perBase: '{price} / {unit}',
    productionDate: 'Produced',
    expiryDate: 'Expires',
    expiryEstimated: 'Estimated — no date from the packaging',
    warehouseLabel: 'Warehouse',
    block: 'Block',
    blockNone: 'No block',
    lineTotal: 'Line total',
    addLine: 'Add line',
    removeLine: 'Remove',
    duplicateLine: 'Duplicate',
    conversionHint: '{quantity} {unit} = {base} {baseUnit}',
    saveDraft: 'Save draft',
    post: 'Post',
    postConfirm: 'Posting adds {total} to the warehouse. The document becomes immutable.',
    cancelDoc: 'Cancel',
    cancelDocument: 'Cancel document',
    chooseProductFirst: 'Choose the product first',
    cancelReason: 'Cancellation reason',
    cancelHint: 'A reason is required. Nothing is deleted — compensating records are written.',
    totalQuantity: 'Total quantity',
    potentialMargin: 'Potential margin',
    batches: 'Batches',
    movements: 'Stock movements',
    audit: 'Audit',
    batchNumber: 'Batch №',
    received: 'Received',
    remaining: 'Remaining',
    allWarehouses: 'All',
    moveToBlock: 'Move to block',
    blockMoved: 'Batch moved to block {code}',
    movementType: 'Type',
    occurredAt: 'Date',
    unitCost: 'Unit cost',
    quantityOnHand: 'On hand',
    reserved: 'Reserved',
    available: 'Available',
    stockValue: 'Stock value',
    batchCount: 'Batches',
    nearestExpiry: 'Nearest expiry',
    level: 'Level',
    daysLeft: '{days} days left',
    expiredDays: 'Expired {days} days ago',
    noExpiry: 'No expiry',
    scanHint: 'Name, SKU or barcode',
    emptyReceipts: 'No receipts yet. Create the first one.',
    emptyStock: 'Nothing in the warehouse yet.',
  },
  docStatus: {
    DRAFT: 'Draft',
    POSTED: 'Posted',
    CANCELLED: 'Cancelled',
  },
  movementType: {
    RECEIPT: 'Receipt',
    SHIPMENT: 'Shipment',
    RETURN_IN: 'Return in',
    RETURN_OUT: 'Return out',
    WRITE_OFF: 'Write-off',
    ADJUSTMENT: 'Adjustment',
    REVERSAL: 'Reversal',
  },
  stockLevel: {
    OUT: 'Out of stock',
    LOW: 'Low',
    NORMAL: 'Normal',
    OVER: 'Overstocked',
  },
  expiry: {
    EXPIRED: 'Expired',
    CRITICAL: 'Critical',
    WARNING: 'Warning',
    WATCH: 'Watch',
    OK: 'OK',
  },
  writeOff: {
    title: 'Write-offs',
    new: 'New write-off',
    empty: 'No write-offs',
    reasonLabel: 'Reason',
    reason: {
      EXPIRED: 'Expired',
      DAMAGED: 'Damaged',
      LOST: 'Lost',
      INTERNAL: 'Internal use',
      OTHER: 'Other',
    },
    loss: 'Loss',
    issuedTo: 'Issued to',
    issuedToStaff: 'Employee',
    issuedToStaffHint: 'Pick from the list — their debt and their pay then sit in one place',
    issuedToStaffPlaceholder: 'Choose an employee',
    asDebt: 'Taken as a debt',
    asDebtHint:
      "The goods' cost goes onto this person's account and can be held back from their pay. Unticked, the business absorbs it.",
    issuedToHint:
      'A name or a department: “Director — home”, “Office”, “Driver Alisher”. Required — an expense with nobody on it becomes a loss nobody can account for.',
    unitCost: 'Unit cost',
    batchHint: 'You choose which lot spoiled — the system does not choose for you.',
    draftHint: 'A draft is saved first. Nothing leaves the warehouse until it is posted.',
    posted: '{number} posted',
    cancelled: 'Write-off cancelled',
    cancelHint: 'The goods return to their own lot, with its expiry date.',
  },
  return: {
    quarantine: 'Quarantine',
    quarantineHint:
      'Goods returned by a shop wait here: in the building, not for sale. Look at them and pick one of the three ways out.',
    quarantineEmpty: 'Nothing in quarantine.',
    backOnSale: 'Back on sale',
    restocked: 'Back on sale',
    title: 'Returns',
    new: 'New return',
    empty: 'No returns',
    directionLabel: 'Direction',
    direction: {
      FROM_STORE: 'From a shop',
      TO_SUPPLIER: 'To a supplier',
    },
    party: 'Counterparty',
    unitPrice: 'Price',
    perBaseHint: '1 {unit} = {price}',
    batchHint: 'Returned goods go back to their own lot, with its expiry date.',
    posted: '{number} posted',
    cancelled: 'Return cancelled',
    cancelHint: 'Stock and debt are reversed together. Both documents stay in the history.',
    override: 'Override the ceiling',
    overrideHint:
      'The shop is returning more than it was delivered from this lot. Only a director may post it, with a reason, and it lands in the audit log. The goods are recorded as a new lot.',
  },
  adjustment: {
    title: 'Adjustments',
    subtitle: 'A stocktake difference. Director only, with a reason.',
    new: 'New adjustment',
    empty: 'No adjustments',
    delta: 'Difference',
    deltaHint: 'A negative number reduces, a positive one increases. For example: -3',
    reasonHint: 'At least 5 characters. This is what explains the number afterwards.',
    posted: '{number} recorded',
  },
  kpi: {
    title: 'KPI',
    metricLabel: 'Metric',
    deleteConfirm: 'Delete the plan? All of its monthly parts go too.',
    collapse: 'Collapse',
    expand: 'Expand',
    yearTarget: 'Year target',
    setTarget: 'Set target',
    weight: 'Weight',
    weightHint:
      'How much this metric counts in the month’s KPI score; each metric is capped at 100 %',
    target: 'Target',
    fact: 'Actual',
    percent: 'Percent',
    split: 'Split into months',
    empty: 'No targets set.',
    half: 'Half {index}',
    quarter: 'Quarter {index}',
    metric: {
      SHIPPED: 'Shipped',
      COLLECTED: 'Collected',
      COLLECTION_RATE: 'Collection rate',
      NEW_STORES: 'New shops',
      VISITS: 'Visits',
      ACTIVE_STORES: 'Active shops',
      OVERDUE_DEBT: 'Overdue debt',
    },
    month: {
      1: 'January',
      2: 'February',
      3: 'March',
      4: 'April',
      5: 'May',
      6: 'June',
      7: 'July',
      8: 'August',
      9: 'September',
      10: 'October',
      11: 'November',
      12: 'December',
    },
  },
  salary: {
    title: 'How the pay is worked out',
    newScheme: 'Add a scheme',
    editScheme: 'Edit the scheme',
    noScheme: 'No scheme has been written for this person.',
    validFrom: 'Valid from',
    validFromHint: 'Months from this date onward are computed with this scheme',
    payable: 'Total (before the advance)',
    apply: 'Use these figures',
    overdueInfoTitle: 'Overdue debts — for information; the manager decides',
    field: {
      base_salary: 'Base salary',
      sales_pct: 'Of what was shipped, %',
      collection_pct: 'Of what was collected, %',
      new_store_bonus: 'Per new shop',
      kpi_bonus: 'KPI bonus',
      kpi_bonus_min_pct: 'Minimum for the KPI bonus, %',
      visit_min_pct: 'Minimum of the visit plan, %',
      visit_penalty: 'Visit penalty',
    },
    line: {
      base: 'Base salary',
      sales: 'Bonus on what was shipped',
      collection: 'Bonus on what was collected',
      newStores: 'For new shops',
      kpi: 'KPI bonus',
      visitPenalty: 'Visit plan not met',
      overdueInfo: 'Overdue debt',
    },
    basis: {
      amount: 'Amount',
      pct: 'Rate',
      count: 'Count',
      each: 'Each',
      average: 'Average',
      minimum: 'Minimum',
      done: 'Achieved',
      returned: 'Returned',
      store: 'Shop',
      days: '{n} days',
    },
  },
  settings: {
    field: {
      tab: 'Field work',
      title: 'Field work settings',
      lede: 'An agent’s round is judged by these settings. A change applies at the next screen refresh — nothing is recomputed.',
      radius: 'Shop radius, metres',
      radiusHint:
        'An order inside this ring counts as written at the shop. The phone’s accuracy is added on top, up to 50 m',
      overdueDays: 'Forgotten after, days',
      overdueHint: 'A shop nobody has visited for this long joins the forgotten list',
      newLimit: 'New shop credit limit',
      newLimitHint:
        'A shop opened from the bot starts with this limit — a credit decision is not made in a doorway',
      blocks: 'Refuse an order written away from the shop',
      blocksHint:
        'Off today: the order goes through and is merely flagged. Switched on, an order outside the radius is refused outright — on a bad-GPS day the agent cannot work. Exempt agents are the exception.',
      wholeNumbers: 'The radius, the days and the GPS accuracy must be whole numbers above zero.',
      maxAccuracy: 'GPS accuracy limit, metres',
      maxAccuracyHint:
        'When an agent marks a shop and the phone’s accuracy is worse than this, the mark is refused. The owner’s ruling: 20 m — at the door, not across the road',
      newStoreBonus: 'Compute the new-shop payment',
      newStoreBonusHint:
        'When on, the salary scheme’s “per new shop” amount is paid in the month the office confirmed the shop. Off for now',
      saved: 'Settings saved',
    },
  },
  supervision: {
    title: 'Supervision',
    subtitle: 'Where the agents were today',
    today: "Today's visits",
    overdue: 'Forgotten shops',
    anomalies: 'Suspicious',
    at: 'Time',
    day: 'Date',
    distance: 'Distance',
    outcome: 'Outcome',
    metres: '{distance} m',
    orders: '{count} orders',
    nothingCameOfIt: 'No order',
    lastSeen: 'Last visit',
    daysAgo: 'Days ago',
    daysOf: '{days} days (expected every {of})',
    neverVisited: 'Never visited',
    storeCount: 'Shops',
    storesInAMinute: '{count} shops in one minute',
    noVisits: 'No visits recorded that day.',
    noOverdue: 'No forgotten shops.',
    noAnomalies: 'No suspicious visits.',
    reason: {
      CLOSED: 'It was closed',
      NO_NEED: 'No goods needed',
      NO_MONEY: 'No money',
      OWNER_AWAY: 'The owner was away',
      OTHER: 'Another reason',
    },
  },
  order: {
    waitingTitle: 'Requests waiting for an answer: {count}',
    confirm: 'Confirm',
    confirmed: 'Order confirmed — the stock is held',
    title: 'Orders',
    new: 'New order',
    edit: 'Edit order',
    empty: 'No orders',
    openOnly: 'Awaiting an answer',
    offSite: 'Written away from the shop',
    offSiteOnly: 'Only written away from the shop',
    writtenAtShop: 'Written at the shop',
    metresAway: '{distance} m away',
    whereWritten: 'Where it was written',
    notMeasured: 'Not measured',
    allOrders: 'All',
    available: 'Available',
    reserved: 'Reserved',
    expectedDate: 'Expected date',
    overAvailable: 'At most {available} can be ordered',
    accept: 'Accept',
    accepted: 'Order accepted',
    reject: 'Reject',
    closed: 'Order closed',
    closeHint:
      'The reason is told to the shop and stays on the document. The order is not deleted — it is closed, and what it held is released.',
    makeShipment: 'Create shipment',
    shipmentMade: 'Shipment',
    formHint:
      'An order does not change stock. It is a request — goods leave when a shipment is posted.',
    status: {
      REQUESTED: 'Requested',
      NEW: 'New',
      ACCEPTED: 'Accepted',
      FULFILLED: 'Fulfilled',
      REJECTED: 'Rejected',
      CANCELLED: 'Cancelled',
      EXPIRED: 'Expired',
    },
  },
  expense: {
    title: 'Expenses',
    subtitle: 'Rent, utilities, repairs — money out that buys no stock',
    new: 'New expense',
    empty: 'No expenses',
    month: 'Month',
    newAdvance: 'Pay an advance',
    allKinds: 'All',
    staff: '(staff)',
    staffPayee: 'Employee',
    staffPayeeHint: 'Pick from the list — this person can then be filtered on',
    salaryPayeeHint: 'A wage needs the person — the system has to know whose it is',
    advanceHint: 'Recorded as an advance: payday subtracts it from this person automatically.',
    staffPlaceholder: 'Choose an employee (optional)',
    category: 'Category',
    paidTo: 'Paid to',
    paidToHint: 'The landlord, the gas company, a repairman — a name or an organisation',
    totalSpent: 'Total spent',
    recorded: '{number} recorded',
    notAPaymentHint:
      'Careful: money paid to a supplier for GOODS does not belong here — that is a payment in Kassa. Recording it twice halves the reported profit.',
  },
  payroll: {
    title: 'Payroll',
    subtitle: 'Per person: salary, bonus, and what is deducted against a debt',
    employees: 'Employees',
    newEmployee: 'New employee',
    employee: 'Employee',
    position: 'Position',
    baseSalary: 'Salary',
    owes: 'Owes',
    owesHint: 'This person owes {amount}. Deducting it is optional — it can wait for next month.',
    paidOut: 'Paid',
    pay: 'Pay',
    finish: 'Finish',
    paid: '{number} paid',
    done: 'Paid',
    empty: 'No employees',
    monthTotal: 'This month',
    bonus: 'Bonus',
    bonusNote: 'Bonus note',
    deduction: 'Deduction',
    advance: 'Advance',
    overAdvanced: 'More was advanced this month than was earned',
    tooMuch: 'More than is owed',
    net: 'Takes home',
    earnedHint:
      'The expense is salary + bonus — that is what was earned. The deduction settles the debt; the rest leaves the drawer.',
    scoreTitle: 'For the bonus — this agent’s figures',
    shipped: 'Shipped',
    collected: 'Collected',
    outstanding: 'Outstanding',
    overdue90: '90+ days',
    agentHint: 'Link an agent and payday shows their collection figures.',
  },
  shipment: {
    postedToast: '{number} posted',
    title: 'Shipments',
    newShipment: 'New shipment',
    editShipment: 'Edit shipment',
    store: 'Store',
    agent: 'Agent',
    dueDate: 'Payment due',
    duePresets: '7 days / 14 days / 30 days',
    balance: 'Current debt',
    creditLimit: 'Credit limit',
    headroom: 'Headroom',
    noLimit: 'No limit',
    projected: 'After this shipment',
    available: 'Available',
    availableBatches: 'Available batches',
    fromWarehouse: 'From warehouse',
    anyWarehouse: 'Any warehouse',
    pinWarehouse: 'Only this warehouse',
    allocations: 'By batch',
    allocation: 'From which batch',
    cogs: 'Cost',
    margin: 'Margin',
    marginPercent: 'Margin %',
    unitChanged: 'Unit changed — price re-derived',
    allowExpired: 'Include expired batches',
    overrideCreditLimit: 'Allow exceeding the credit limit',
    adminOnly: 'Director only',
    overdueOnly: 'Overdue only',
    postConfirm:
      "Posting removes {count} lines from the warehouse and increases {store}'s debt by {total}.",
    emptyShipments: 'No shipments yet. Create the first one.',
    print: 'Print',
    deliveryNote: 'Delivery note',
    warnings: 'Warnings',
  },
  warn: {
    near_expiry: 'Batch {batch} expires in {days_left} days.',
    expired_skipped:
      '{product}: {count} expired batch(es) ({quantity}) skipped — they should be written off.',
    credit_limit_overridden:
      '{store} is past its credit limit ({limit}) — {projected}. Allowed by {by}.',
  },
  finance: {
    shipped: 'Shipped',
    collected: 'Collected',
    balance: 'Balance',
    supplier: 'Supplier',
    store: 'Store',
    agent: 'Agent',
    party: 'Counterparty',
    goodsReceived: 'Goods received',
    goodsShipped: 'Goods shipped',
    paymentsGiven: 'Payments made',
    documentsCount: '{count} documents',
    paymentsCount: '{count} payments',
    theyOweUs: 'They owe us',
    weOweThem: 'We owe them',
    settled: 'Settled',
    lastMovement: 'Last movement',
    amount: 'Amount',
    methodLabel: 'Method',
    paidAt: 'Date',
    paidOn: 'Paid on',
    comment: 'Comment',
    commentHint: 'Optional',
    balanceBefore: 'Balance before',
    balanceAfter: 'Balance after',
    recordPayment: 'Record payment',
    cancelPayment: 'Cancel payment',
    cancelReason: 'Cancellation reason',
    cancelReasonHint: 'e.g. entered twice',
    docNumber: 'Document №',
    date: 'Date',
    document: 'Document',
    note: 'Note',
    debit: 'Debit',
    credit: 'Credit',
    runningBalance: 'Balance',
    openingBalance: 'Opening balance',
    oldestIsOpening: 'Opening balance',
    closingBalance: 'Closing balance',
    dateFrom: 'From',
    dateTo: 'To',
    exportExcel: 'Excel',
    direction: 'Direction',
    incoming: 'Incoming',
    outgoing: 'Outgoing',
    paymentIn: 'Money in',
    paymentOut: 'Money out',
    allDirections: 'All',
    allMethods: 'All methods',
    total: 'Total',
    totalQuantity: 'Total quantity',
    averagePrice: 'Average price',
    lastPrice: 'Last price',
    lastPurchase: 'Last purchase',
    shippedTotal: 'Shipped',
    collectedTotal: 'Collected',
    exposure: 'Outstanding',
    storesServed: 'Stores',
    debt: 'Debt',
    oldestUnpaid: 'Oldest unpaid document',
    daysOverdue: 'Days overdue',
    collectionRate: 'Collection rate',
    averageCollectionDays: 'Average collection days',
    payToSupplier: 'Pay a supplier',
    collectFromStore: 'Collect from a store',
    chooseSupplier: 'Choose a supplier',
    chooseSupplierFirst: 'Choose the supplier first.',
    collectedFrom: 'Collected from',
    chooseStore: 'Choose a store',
    chooseStoreFirst: 'Choose the store first — money always belongs to a store.',
    documentsCountShort: 'Documents',
    receivablesAging: 'Receivables aging',
    method: {
      CASH: 'Cash',
      CLICK: 'Click',
      TRANSFER: 'Transfer',
      CARD: 'Card',
    },
    docType: {
      goods_receipt: 'Receipt',
      shipment: 'Shipment',
      payment: 'Payment',
      OPENING: 'Opening balance',
    },
    aging: {
      not_due: 'Not due',
      d1_30: '1–30 days',
      d31_60: '31–60 days',
      d61_90: '61–90 days',
      d90_plus: '90+ days',
    },
    tab: {
      receipts: 'Receipts',
      shipments: 'Shipments',
      payments: 'Payments',
      kassa: 'Kassa',
      statement: 'Statement',
      products: 'Products',
      stores: 'Stores',
      performance: 'Performance',
    },
  },
  goLive: {
    title: 'Go-live',
    intro:
      'Paper stock and debts enter the system as documents. Check the three totals against your records before locking.',
    step1: 'Step 1 · Go-live date',
    step2: 'Step 2 · Pre-lock summary',
    step3: 'Step 3 · Lock',
    dateLabel: 'Go-live date',
    saveDate: 'Save',
    openingStock: 'Opening stock value',
    stockDetail: '{batches} batches · {products} products',
    totalReceivable: 'Receivable (stores)',
    totalPayable: 'Payable (suppliers)',
    storesCount: '{count} stores',
    suppliersCount: '{count} suppliers',
    lockHint:
      'After locking, only the director may change opening documents. Retype both totals to confirm.',
    confirmReceivable: 'Retype the receivable total',
    confirmPayable: 'Retype the payable total',
    lockButton: 'Lock',
    locked: 'Go-live locked on {date}.',
  },
  dashboard: {
    vsPreviousPeriod: 'Against the previous period',
    openList: 'Open the list',
    overdue90: '90+ days',
    inQuarantine: 'Of which in quarantine',
    valueAtRisk: 'Value at risk',
    daysLeft: 'Days left',
    daysCounted: '{count} days',
    goodsIn: 'Goods in',
    goodsOut: 'Goods out',
    revenue: 'Revenue',
    cogs: 'COGS',
    grossProfit: 'Gross profit',
    margin: 'Margin',
    deadStock: 'Dead stock',
    period: {
      today: 'Today',
      week: '7 days',
      month: '30 days',
      quarter: 'Quarter',
      year: 'Year',
      custom: 'Range',
    },
    measure: { amount: 'Amount', quantity: 'Quantity' },
    kpi: {
      stock_value: 'Stock value',
      receivable: 'Receivable',
      payable: 'Payable',
      net_position: 'Net position',
      period_in: 'Goods in',
      period_out: 'Goods out',
    },
    otherCosts: 'Internal use and losses',
    openOrders: 'Open orders',
    orderValue: 'Order value',
    awaitingAnswer: 'Awaiting an answer',
    reservedProducts: 'Stock held for {count} products',
    monthWages: "This month's wages",
    paidSoFar: 'Paid',
    staffDebt: 'Staff debt',
    staffDebtHint: 'Goods taken as a debt — settled by a deduction from pay',
    card: {
      expiry: 'Expiry',
      lowStock: 'Low stock',
      overstock: 'Overstock',
      dynamics: 'In / out dynamics',
      storeDebt: 'Store debt',
      agentDebt: 'Debt by agent',
      topProducts: 'Top products',
      profit: 'Profit',
      orders: 'Orders',
      staff: 'Payday',
      overdue: 'Overdue payments',
      turnover: 'Inventory turnover',
      activity: 'Recent activity',
      collection: 'Cash collection',
      overLimit: 'Over the credit limit',
      dormant: 'Shops gone quiet',
      fillRate: 'Order fill rate',
      frozen: 'Money standing still',
    },
    collected: 'collected',
    shippedOnCredit: 'Shipped on credit',
    cameToKassa: 'Came through the kassa',
    notYetCollected: 'Not collected this period',
    outstandingAll: 'Outstanding (all time)',
    overLimitHint: 'Shipping to these shops should stop',
    nobodyOverLimit: 'Nobody is over their limit.',
    overBy: 'Over by',
    dormantHint: 'No orders for {days} days',
    everybodyBuying: 'Every shop is buying.',
    lastOrder: 'Last shipment',
    quietFor: 'Quiet for',
    lastYearValue: 'Value last year',
    days: '{n} days',
    fillRateHint: 'Asked for, and shipped',
    noOrdersInPeriod: 'No orders in this period.',
    ofOrders: '{done} of {all} fulfilled',
    ordered: 'Ordered',
    shipped: 'Shipped',
    stillOpen: 'Open: {n}',
    lost: 'Lost: {n}',
    frozenHint: 'Not moved for {days} days',
    everythingMoving: 'Everything is moving.',
    frozenTotal: 'sitting in the warehouse',
  },
  reports: {
    'stock-valuation': 'Stock valuation',
    expiry: 'Expiry',
    'receivables-aging': 'Receivables aging',
    payables: 'Payables',
    sales: 'Sales',
    profit: 'Profit',
    'net-profit': 'Net profit',
    averageCost: 'Average cost',
    value: 'Value',
    expiryDate: 'Expiry date',
    bucket: 'Bucket',
    name: 'Name',
    inQuarantine: 'Of which in quarantine',
    quarantineLots: '{n} lots',
    groupBy: {
      product: 'By product',
      store: 'By store',
      agent: 'By agent',
      month: 'By month',
    },
  },
  netProfit: {
    revenue: 'Revenue',
    cogs: 'Cost of goods',
    gross: 'Gross profit',
    marginHint: '{percent} margin',
    expenses: 'Operating costs',
    expensesHint: 'rent, wages, utilities — wages are already included',
    internal: 'Internal use',
    internalHint: 'given away — goods taken as a debt are not here',
    losses: 'Losses',
    net: 'NET PROFIT',
    netHint: '{percent} of revenue',
    chargedHint: 'Goods employees owe for (not in this report — they sit on their accounts):',
  },
  company: {
    title: 'Company details',
    subtitle: 'Printed on documents and shown in the sidebar. Filled in once.',
    name: 'Company name',
    nameHint: 'The short name — it goes in the document header',
    legalName: 'Full legal name',
    address: 'Address',
    phone: 'Phone',
    email: 'Email',
    taxId: 'Tax ID',
    bank: 'Bank details',
    bankName: 'Bank name',
    bankAccount: 'Account number',
    logo: 'Logo',
    noLogo: 'No logo set',
    removeLogo: 'Remove',
    logoHint: 'PNG, JPG or SVG. Under {limit} KB — it is a page header, not a photograph.',
    logoTooBig: 'That file is too large. Up to {limit} KB.',
    saved: 'Company details saved',
  },
  palette: {
    title: 'Command palette',
    placeholder: 'A screen, product, store or supplier…',
    group: { screen: 'Screen', product: 'Product', store: 'Store', supplier: 'Supplier' },
  },
  shortcuts: {
    title: 'Keyboard shortcuts',
    global: 'Global',
    documents: 'Document forms',
    tables: 'Tables',
    palette: 'Command palette',
    sheet: 'Shortcut sheet',
    close: 'Close',
    saveDraft: 'Save draft',
    post: 'Post',
    newLine: 'New line',
    moveRow: 'Move between rows',
    openRow: 'Open row',
  },
  print: {
    company: 'OMBOR LLC',
    companyAddress: 'Tashkent, main warehouse',
    counterparty: 'Counterparty',
    printedAt: 'Printed',
    receipt: 'Goods receipt',
    deliveryNote: 'Delivery note',
    statement: 'Reconciliation statement',
    paymentReceipt: 'Payment receipt',
    signedBy: 'Handed over by',
    receivedBy: 'Received by',
    accountant: 'Accountant',
  },
  errors: {
    AGENT_NOT_DISMISSABLE:
      'Cannot be dismissed: shops are still assigned, or a payroll is unfinished.',
    PAYROLL_EXPENSE_LOCKED:
      'This expense was written by a payroll and cannot be cancelled on its own.',
    SHOP_PHONE_SHARED: 'More than one shop has this phone number. Contact the office.',
    apiUnreachable: 'The server is unreachable. Nothing you enter is being saved.',
    title: 'Error',
    INVALID_CREDENTIALS: 'Wrong username or password',
    ACCOUNT_LOCKED: 'Account is temporarily locked. Try again later.',
    ACCOUNT_INACTIVE: 'This account has been deactivated. Contact an administrator.',
    INVALID_TOKEN: 'Session expired. Please sign in again.',
    TOKEN_REUSE_DETECTED: 'All sessions were ended for security. Please sign in again.',
    CSRF_FAILED: 'Request could not be verified. Reload the page.',
    PERMISSION_DENIED: 'You do not have permission for this action.',
    PASSWORD_CHANGE_REQUIRED: 'Change your temporary password first.',
    CANCEL_WINDOW_CLOSED:
      'More than 24 hours have passed since this document was posted — only the director can cancel it now.',
    CANCEL_DIRECTOR_ONLY: 'Only the director can cancel this document.',
    NOT_FOUND: 'Not found.',
    CONFLICT: 'This record already exists.',
    WEAK_PASSWORD: 'Password does not meet the requirements.',
    RATE_LIMITED: 'Too many attempts. Please wait a moment.',
    VALIDATION_ERROR: 'There is a problem with the data.',
    SERVICE_UNAVAILABLE: 'The server is not responding.',
    NETWORK: 'Could not reach the server.',
    ORDER_STATE: 'The order’s current state does not allow that.',
    NOT_AT_THE_SHOP: 'You are not at «{store}»: {distance_m} m away.',
    PRICE_CHANGED: 'The price of «{product}» changed: shown {shown}, now {current}.',
    KPI_PLAN_SHAPE: 'The KPI plan does not fit its period, or its parts no longer add up.',
    TRANSFER_DATE_IN_FUTURE: 'A transfer cannot be dated in the future: {effective_date}.',
    TRANSFER_DATE_TOO_EARLY:
      '“{store}” has been with its current agent since {since}; date the transfer after that (earliest {earliest}).',
    SCHEME_MONTH_PAID:
      'The payroll for {month} is already posted (through {paid_through}). Write the scheme from the next month.',
    LOCATION_ALREADY_SET:
      '«{store}» already has a position. If the shop has moved, tell the manager.',
    LOCATION_PROPOSAL_PENDING: 'A relocation report is already waiting on this shop.',
    LOCATION_PROPOSAL_DECIDED: 'This report has already been decided ({status}).',
    LOCATION_TOO_COARSE:
      'The GPS fix is not good enough ({accuracy_m} m, {limit_m} m allowed). Send it again at the shop door.',
    PRODUCT_UNPRICED: '«{product}» has no price — a shop cannot request it.',
    ORDER_POSITION_REQUIRED:
      'An order for «{store}» is taken only at the shop: send your location.',
    OFF_SITE_ORDER:
      'This order was written {distance_m} m from the shop. The fence is {radius_m} m.',
    NOT_ENOUGH_TO_PROMISE:
      'More was ordered than can be promised: {available} available, {requested} requested.',
    INSUFFICIENT_BATCH: 'Batch “{batch}” holds {available}; {requested} was asked for.',
    RETURN_EXCEEDS_DELIVERY:
      'This shop received {delivered} from batch “{batch}” and has returned {already_returned} — {available} may still come back, but {requested} was asked for.',
    DOCUMENT_STATE: 'The document’s current state does not allow that.',
    PAYMENT_STATE: 'The payment’s current state does not allow that.',
    GO_LIVE_LOCKED: 'Go-live is locked. Only a director can change it.',
    GO_LIVE_NOT_READY: 'Go-live is not finished yet.',
    IDEMPOTENCY_CONFLICT: 'This request was already sent with different data.',
    INSUFFICIENT_STOCK: 'Not enough stock in the warehouse.',
    CREDIT_LIMIT_EXCEEDED: 'This exceeds the credit limit.',
    INVALID_DOCUMENT_STATE: 'The document status does not allow this action.',
    DOCUMENT_HAS_NO_LINES: 'The document has no lines.',
    BATCH_ALREADY_CONSUMED:
      'Some of these goods have already left the warehouse. Reverse these documents first: {documents}',
    MISSING_EXCHANGE_RATE: 'No exchange rate on file for this date.',
    FRACTIONAL_BASE_UNIT: 'This unit cannot hold a fractional quantity.',
    BASE_UNIT_LOCKED: 'The base unit cannot be changed.',
    ENTITY_IN_USE: 'This record is in use and cannot be deleted.',
    BATCH_PRODUCT_MISMATCH:
      'Lot «{batch}» belongs to a different product. Choose a lot of this product.',
    BATCH_BLOCKED: 'Lot «{batch}» comes from a cancelled receipt — nothing can be added to it.',
    BATCH_OVER_RECEIVED:
      'Lot «{batch}» received {received} and now holds {remaining} — adding {requested} would exceed what was received. Enter the surplus as a new receipt.',
    RETURNS_EXIST:
      'The shop has returned part of this shipment. Cancel those returns first: {documents}',
    PARTY_HAS_BALANCE:
      'This counterparty still has a balance of {balance} — it cannot be deleted until it is settled.',
    SYSTEM_ENTITY: 'This is a system record and cannot be deleted.',
    DEFAULT_WAREHOUSE:
      'The default warehouse cannot be deleted or demoted — make another one the default first.',
    NO_STOCK_TO_REMOVE: 'There is no stock to remove.',
    IMPORT_FILE_INVALID: 'The file could not be read.',
    ADVANCE_EXCEEDS_EARNINGS:
      'An advance of {advance} has already been paid this month — this pay ({earned}) is less.',
    AGENT_REQUIRED: 'Choose the agent who brought the money.',
    BASE_CURRENCY_RATE: 'The rate of the base currency is always 1 — it cannot be entered.',
    BASE_CURRENCY_REQUIRED: 'A base currency is required — make another currency the base first.',
    BASE_UNIT_AS_PACKAGE: 'The base unit cannot be added as a package — it always equals 1.',
    BATCH_SUPPLIER_MISMATCH: 'Lot «{batch}» came from a different supplier.',
    BOT_SECRET_INVALID: 'The bot key is invalid.',
    CATEGORY_OWN_PARENT: 'A category cannot be its own parent.',
    CONSTRAINT_VIOLATED: 'The database rejected this change. Refresh the page and try again.',
    DEBTOR_REQUIRED: 'Choose the employee who owes for goods issued on credit.',
    DEDUCTION_EXCEEDS_DEBT: 'The deduction ({deduction}) is more than the employee owes ({owed}).',
    DEDUCTION_EXCEEDS_PAY:
      'The pay left after advances ({remaining}) is less than the deduction ({deduction}).',
    DEFAULT_UNIT_INVALID:
      'The default unit must be the base unit or one of the product’s packages.',
    EXPENSE_STATE: 'The expense status does not allow this action.',
    FOREIGN_KEY_IN_USE:
      'A linked record was not found or is still in use. Refresh the page and try again.',
    INTERNAL_ERROR:
      'An unexpected error occurred and has been logged. If it persists, contact the administrator.',
    INVALID_VALUE: 'The value entered is too large or invalid.',
    LOCATION_RETIRED: 'Block «{location}» has been retired.',
    LOCATION_WRONG_WAREHOUSE: 'Block «{location}» belongs to a different warehouse.',
    LOGO_TOO_LARGE: 'The logo file is too large.',
    OVERRIDE_REASON_REQUIRED: 'Give a reason for overriding the limit.',
    PAYROLL_STATE: 'The payroll status does not allow this action.',
    RECIPIENT_REQUIRED: 'For an internal issue, say who received the goods.',
    SALARY_NEEDS_A_PERSON: 'Choose the employee for a salary expense.',
    SELF_DEACTIVATION: 'You cannot deactivate your own account.',
    SELF_DELETION: 'You cannot delete your own account.',
    SELF_ROLE_CHANGE: 'You cannot change your own role.',
    SHOP_ALREADY_LINKED: 'This Telegram account is already linked to another shop.',
    SHOP_NOT_LINKED: 'The shop is not linked to Telegram.',
    STORE_REQUIRED: 'Choose the shop for a return from a shop.',
    SUPPLIER_PAYMENT_AGENT: 'A payment to a supplier does not name an agent.',
    SUPPLIER_REQUIRED: 'Choose the supplier for a return to a supplier.',
    TELEGRAM_ALREADY_LINKED: 'This Telegram account is already linked to another user.',
    TELEGRAM_NOT_LINKED: 'The Telegram account is not linked.',
    UNIT_LISTED_TWICE: 'The same unit is listed twice.',
    UNIT_NOT_CONFIGURED: 'Product «{product}» has no such unit of measure.',
    UNKNOWN_ERROR: 'An unexpected error occurred.',
  },
  password: {
    too_short: 'At least 8 characters',
    too_common: 'This password is too common',
    contains_username: 'Must not contain the username',
    digits_only: 'Must not be digits only',
    mismatch: 'Passwords do not match',
  },
  pages: {
    forbidden: {
      title: 'No access',
      message: 'You do not have permission to view this page.',
    },
    notFound: {
      title: 'Page not found',
      message: 'The requested address does not exist.',
      back: 'Back to dashboard',
    },
    dashboard: {
      placeholder: 'The foundation is ready. Modules arrive phase by phase.',
      apiChecking: 'API: checking…',
      apiDown: 'API: unreachable',
      apiUp: 'API: healthy',
    },
  },
}

export default en
